import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export interface DealScenario {
  id: string;
  name: string;
  headline_price: number;
  business_name: string;
  bank_amount: number;
  bank_rate_annual: number;
  bank_term_years: number;
  seller_amount: number;
  seller_rate_annual: number;
  seller_structure: 'amort' | 'io' | 'io_then_amort';
  seller_amortization_years?: number;
  seller_balloon_years: number;
  seller_balloon_due_month: number;
  seller_io_months?: number;
  annual_sde: number;
  monthly_sde: number;
  financial_metric_type: string;
  annual_threshold: number;
  quarterly_threshold: number;
  created_at: string;
  updated_at: string;
}

export interface EscrowDetails {
  id: string;
  deal_scenario_id: string;
  initial_deposit_from_seller: number;
  quarterly_contribution_if_quarterly_metric_over?: number;
  contribution_rate_on_excess?: number;
  monthly_draws_allowed_if_monthly_metric_negative?: boolean;
  balloon_release_if_note_current?: boolean;
  buyer_draw_period_months?: number;
  buyer_draw_condition_amount?: number;
  buyer_draw_catchup_description?: string;
  buyer_draw_return_condition?: string;
  buyer_draw_validity?: string;
}

export interface EscrowMilestone {
  id: string;
  escrow_details_id: string;
  month: number;
  release_amount: number;
  condition_description: string;
}

export interface SellerUpside {
  id: string;
  deal_scenario_id: string;
  profit_share_percent_of_metric_over_threshold?: number;
  profit_share_annual_metric_threshold?: number;
  profit_share_lifetime_cap?: number;
  exit_kicker_percent_above_valuation?: number;
  exit_kicker_valuation_floor?: number;
  exit_kicker_lifetime_cap?: number;
  exit_kicker_definition?: string;
  exit_kicker_validity?: string;
  exit_kicker_disclaimer?: string;
}

export interface MonthlyPaymentSchedule {
  id: string;
  deal_scenario_id: string;
  month_index: number;
  bank_payment: number;
  bank_interest: number;
  bank_principal: number;
  bank_balance: number;
  seller_payment: number;
  seller_interest: number;
  seller_principal: number;
  seller_balance: number;
  total_debt_service: number;
  assumed_monthly_sde: number;
  post_debt_cash_flow: number;
}

export interface SellerPayoutsSummary {
  id: string;
  deal_scenario_id: string;
  seller_interest_paid_to_balloon: number;
  seller_principal_repaid_to_balloon: number;
  seller_balloon_balance: number;
  seller_net_at_closing: number;
  seller_total_collected_no_escrow: number;
  seller_total_collected_with_escrow: number;
  seller_total_potential_with_exit_kicker?: number;
  cash_at_closing_net?: number;
  wc_escrow_release?: number;
  interest_collected_period?: number;
  principal_payments_period?: number;
  balloon_payment_amount?: number;
  exit_kicker_capped?: number;
  total_potential?: number;
}

export interface NarrativeSection {
  id: string;
  scenario_narrative_id: string;
  title: string;
  content: string[];
  section_order: number;
}

export interface ScenarioNarrative {
  id: string;
  deal_scenario_id: string;
  narrative_type: string;
  scenario_name: string;
  sections: NarrativeSection[];
}

export interface CompleteDealScenario extends DealScenario {
  escrow: EscrowDetails;
  escrow_milestones: EscrowMilestone[];
  seller_upside: SellerUpside;
  monthly_schedule: MonthlyPaymentSchedule[];
  seller_payouts: SellerPayoutsSummary;
  narratives: ScenarioNarrative[];
}

export function useDealScenarios() {
  const [scenarios, setScenarios] = useState<CompleteDealScenario[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchDealScenarios() {
      try {
        setLoading(true);
        setError(null);

        // Fetch deal scenarios first
        const { data: scenariosData, error: scenariosError } = await supabase
          .from('deal_scenarios')
          .select('*')
          .order('name');

        if (scenariosError) {
          throw scenariosError;
        }
        if (!scenariosData || scenariosData.length === 0) {
          setScenarios([]);
          return;
        }

        // Fetch related data for each scenario
        const transformedScenarios: CompleteDealScenario[] = await Promise.all(
          scenariosData.map(async (scenario: any) => {
            // Fetch escrow details
            const { data: escrowData } = await supabase
              .from('escrow_details')
              .select('*, escrow_milestone_releases(*)')
              .eq('deal_scenario_id', scenario.id)
              .maybeSingle();

            // Fetch seller upside
            const { data: sellerUpsideData } = await supabase
              .from('seller_upside')
              .select('*')
              .eq('deal_scenario_id', scenario.id)
              .maybeSingle();

            // Fetch monthly schedule
            const { data: monthlyScheduleData } = await supabase
              .from('monthly_payment_schedule')
              .select('*')
              .eq('deal_scenario_id', scenario.id)
              .order('month_index');

            // Fetch seller payouts
            const { data: sellerPayoutsData } = await supabase
              .from('seller_payouts_summary')
              .select('*')
              .eq('deal_scenario_id', scenario.id)
              .maybeSingle();

            // Fetch narratives
            const { data: narrativesData } = await supabase
              .from('scenario_narratives')
              .select('*, narrative_sections(*)')
              .eq('deal_scenario_id', scenario.id);

            return {
              ...scenario,
              escrow: escrowData || {},
              escrow_milestones: escrowData?.escrow_milestone_releases || [],
              seller_upside: sellerUpsideData || {},
              monthly_schedule: monthlyScheduleData || [],
              seller_payouts: sellerPayoutsData || {},
              narratives: narrativesData?.map((narrative: any) => ({
                ...narrative,
                sections: narrative.narrative_sections?.sort((a: any, b: any) => a.section_order - b.section_order) || []
              })) || []
            };
          })
        );

        setScenarios(transformedScenarios);
      } catch (err) {
        console.error('Error fetching deal scenarios:', err);
        setError(err instanceof Error ? err.message : 'An error occurred while fetching deal scenarios');
      } finally {
        setLoading(false);
      }
    }

    fetchDealScenarios();
  }, []);

  return { scenarios, loading, error };
}

export function useDealScenario(scenarioName: string) {
  const { scenarios, loading, error } = useDealScenarios();
  
  const scenario = scenarios.find(s => s.name === scenarioName);
  
  return { scenario, loading, error };
}
